import { CheckMark, Close } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { TypographyColors } from '@lyearn/ui';
import { OptionsListItemTypeEnum } from '@/modules/corporate/modules/questions/fields/single-select/types';

import { renderKeyCodeIcon, renderKeyCodeLabel } from './renderers';
import {
  CORRECT_OPTION_KEY_CODE_BACKGROUND_COLOR,
  correctOptionItemBackground,
  correctOptionItemBorder,
  correctOptionKeyCodeBackground,
  h195,
  INCORRECT_OPTION_KEY_CODE_BACKGROUND_COLOR,
  incorrectOptionItemBackground,
  incorrectOptionItemBorder,
  incorrectOptionKeyCodeBackground,
  PARTIALLY_CORRECT_OPTION_KEY_CODE_BACKGROUND_COLOR,
  partiallyCorrectOptionItemBackground,
  partiallyCorrectOptionItemBorder,
  partiallyCorrectOptionKeyCodeBackground,
} from './styles';

export const BASE_CLASSNAME = 'py-10 px-12 border border-solid rounded-8 xs:flex-wrap';
const BASE_IMAGE_CLASSNAME = cn('border-solid mb-8', h195);

// keycode for "a"
export const FIRST_KEYCODE_INDEX = 65;

type OptionItemConfig = {
  [key in OptionsListItemTypeEnum]: {
    dataTestId?: string;
    labelKey?: string;
    renderKeyCodeContent: (label: string) => React.ReactNode;
    classes: {
      attachments?: string;
      listItem: string;
      optionText: TypographyColors;
      status?: string;
      keyCode: string;
      secondaryBackgroundColor: string;
    };
    styles?: {
      attachments?: TSFixMe;
      listItem?: TSFixMe;
      optionText?: TSFixMe;
      status?: TSFixMe;
      keyCode?: TSFixMe;
    };
  };
};

export const OPTION_ITEM_CONFIG: OptionItemConfig = {
  [OptionsListItemTypeEnum.Correct]: {
    dataTestId: 'correct-option',
    labelKey: 'QuizPlayer:Correct',
    renderKeyCodeContent: () =>
      renderKeyCodeIcon({ Icon: CheckMark, fillColorToken: 'icon-white' }),
    classes: {
      attachments: cn(BASE_IMAGE_CLASSNAME, 'border-2', correctOptionItemBorder),
      keyCode: correctOptionKeyCodeBackground,
      secondaryBackgroundColor: CORRECT_OPTION_KEY_CODE_BACKGROUND_COLOR,
      listItem: cn(BASE_CLASSNAME, correctOptionItemBackground, correctOptionItemBorder),
      optionText: 'text-text-white',
      status: 'text-text-white',
    },
    styles: {
      attachments: [correctOptionItemBorder],
      keyCode: [correctOptionKeyCodeBackground],
      listItem: [correctOptionItemBackground, correctOptionItemBorder],
    },
  },
  [OptionsListItemTypeEnum.Default]: {
    renderKeyCodeContent: (label) => renderKeyCodeLabel({ label, className: 'text-text-primary' }),
    classes: {
      attachments: BASE_IMAGE_CLASSNAME,
      keyCode: 'bg-bg-primary border border-border',
      secondaryBackgroundColor: 'var(--bg-primary)',
      listItem: cn(BASE_CLASSNAME, 'hover:bg-s-info-light bg-bg-secondary border-divider'),
      optionText: 'text-text-primary',
    },
  },
  [OptionsListItemTypeEnum.Incorrect]: {
    dataTestId: 'incorrect-option',
    labelKey: 'QuizPlayer:Incorrect',
    renderKeyCodeContent: () => renderKeyCodeIcon({ Icon: Close, fillColorToken: 'icon-white' }),
    classes: {
      attachments: cn(BASE_IMAGE_CLASSNAME, 'border-2', incorrectOptionItemBorder),
      keyCode: incorrectOptionKeyCodeBackground,
      secondaryBackgroundColor: INCORRECT_OPTION_KEY_CODE_BACKGROUND_COLOR,
      listItem: cn(BASE_CLASSNAME, incorrectOptionItemBackground, incorrectOptionItemBorder),
      optionText: 'text-text-white',
      status: 'text-text-white',
    },
    styles: {
      attachments: [incorrectOptionItemBorder],
      keyCode: [incorrectOptionKeyCodeBackground],
      listItem: [incorrectOptionItemBackground, incorrectOptionItemBorder],
    },
  },
  [OptionsListItemTypeEnum.Missed]: {
    dataTestId: 'missed-option',
    labelKey: 'QuizPlayer:MissedCorrect',
    renderKeyCodeContent: () =>
      renderKeyCodeIcon({ Icon: CheckMark, fillColorToken: 'icon-white' }),
    classes: {
      attachments: cn(BASE_IMAGE_CLASSNAME, 'border-2', correctOptionItemBorder),
      keyCode: correctOptionKeyCodeBackground,
      secondaryBackgroundColor: CORRECT_OPTION_KEY_CODE_BACKGROUND_COLOR,
      listItem: cn(BASE_CLASSNAME, 'bg-bg-secondary', correctOptionItemBorder),
      optionText: 'text-text-primary',
      status: 'text-text-success',
    },
    styles: {
      attachments: [correctOptionItemBorder],
      keyCode: [correctOptionKeyCodeBackground],
      listItem: [correctOptionItemBorder],
    },
  },
  [OptionsListItemTypeEnum.PartiallyCorrect]: {
    dataTestId: 'partially-correct-option',
    labelKey: 'QuizPlayer:PartiallyCorrect',
    renderKeyCodeContent: () =>
      renderKeyCodeIcon({ Icon: CheckMark, fillColorToken: 'icon-white' }),
    classes: {
      attachments: cn(BASE_IMAGE_CLASSNAME, 'border-2', partiallyCorrectOptionItemBorder),
      // Kept a shade darker than the row background so the checkmark badge stays legible against it.
      keyCode: partiallyCorrectOptionKeyCodeBackground,
      secondaryBackgroundColor: PARTIALLY_CORRECT_OPTION_KEY_CODE_BACKGROUND_COLOR,
      listItem: cn(
        BASE_CLASSNAME,
        partiallyCorrectOptionItemBackground,
        partiallyCorrectOptionItemBorder,
      ),
      optionText: 'text-text-primary',
      status: 'text-text-primary',
    },
    styles: {
      attachments: [partiallyCorrectOptionItemBorder],
      keyCode: [partiallyCorrectOptionKeyCodeBackground],
      listItem: [partiallyCorrectOptionItemBackground, partiallyCorrectOptionItemBorder],
    },
  },
  [OptionsListItemTypeEnum.PartiallyCorrectMissed]: {
    dataTestId: 'partially-correct-missed-option',
    labelKey: 'QuizPlayer:PartiallyCorrect',
    renderKeyCodeContent: () =>
      renderKeyCodeIcon({ Icon: CheckMark, fillColorToken: 'icon-white' }),
    classes: {
      attachments: cn(BASE_IMAGE_CLASSNAME, 'border-2', partiallyCorrectOptionItemBorder),
      keyCode: partiallyCorrectOptionKeyCodeBackground,
      secondaryBackgroundColor: PARTIALLY_CORRECT_OPTION_KEY_CODE_BACKGROUND_COLOR,
      listItem: cn(BASE_CLASSNAME, 'bg-bg-secondary', partiallyCorrectOptionItemBorder),
      optionText: 'text-text-primary',
      status: 'text-text-warning',
    },
    styles: {
      attachments: [partiallyCorrectOptionItemBorder],
      keyCode: [partiallyCorrectOptionKeyCodeBackground],
      listItem: [partiallyCorrectOptionItemBorder],
    },
  },
  [OptionsListItemTypeEnum.Selected]: {
    renderKeyCodeContent: (label) => renderKeyCodeLabel({ label, className: 'text-text-white' }),
    classes: {
      attachments: cn(BASE_IMAGE_CLASSNAME, 'border-2 border-interactive-primary'),
      keyCode: 'bg-interactive-primary-active',
      secondaryBackgroundColor: 'var(--interactive-primary-active)',
      listItem: cn(BASE_CLASSNAME, 'bg-interactive-primary border-interactive-primary'),
      optionText: 'text-text-white',
    },
  },
  [OptionsListItemTypeEnum.Answered]: {
    renderKeyCodeContent: (label) => renderKeyCodeLabel({ label, className: 'text-text-primary' }),
    classes: {
      attachments: cn(BASE_IMAGE_CLASSNAME, 'border-2 border-interactive-primary'),
      keyCode: 'bg-bg-primary border border-border',
      secondaryBackgroundColor: 'var(--bg-primary)',
      listItem: cn(BASE_CLASSNAME, 'bg-bg-secondary border-interactive-primary'),
      optionText: 'text-text-primary',
    },
  },
};
